import React, { useState, useRef, useLayoutEffect } from "react";
import { Dropdown, DropdownButton, Form, Button, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createPortal } from "react-dom";
import styles from "../../_css/FilterBar.module.css";
import SelectComponent from "../common/SelectComponent";



const FilterBar = ({ onFilterChange, showExtraFilter = true, showEndDate = true, filter }) => {
  const [filterType, setFilterType] = useState(filter?.filterType || null);
  const [startDate, setStartDate] = useState(filter?.startDate || null);
  const [endDate, setEndDate] = useState(filter?.endDate || null);
  const [showEndDatePrompt, setShowEndDatePrompt] = useState(false);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const handleFilterType = (type) => {
    setFilterType(type.value);
    if (onFilterChange)
      onFilterChange({ filterType: type.value, startDate, endDate });
  };

  const handleStartDate = (date) => {
    console.log(date);
    setStartDate(date);
    // Small delay to ensure the date is properly set before closing
    setTimeout(() => {
      setIsStartDatePickerOpen(false);
    }, 100);
    if (!endDate && showEndDate) setShowEndDatePrompt(true);
    if (onFilterChange)
      onFilterChange({ filterType, startDate: date, endDate });
  };

  const handleEndDate = (date) => {
    setEndDate(date);
    // Small delay to ensure the date is properly set before closing
    setTimeout(() => {
      setIsEndDatePickerOpen(false);
    }, 100);
    setShowEndDatePrompt(false);
    if (onFilterChange)
      onFilterChange({ filterType, startDate, endDate: date });
  };

  const handleStartDateInputClick = () => {
    setIsStartDatePickerOpen(true);
    setIsEndDatePickerOpen(false);
  };

  const handleEndDateInputClick = () => {
    setIsEndDatePickerOpen(true);
    setIsStartDatePickerOpen(false);
  };

  // Close date pickers when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is on a date picker element
      const isDatePickerClick = event.target.closest('.react-datepicker') || 
                               event.target.closest('[data-testid="day"]') ||
                               event.target.closest('.react-datepicker__day');
      
      if (isDatePickerClick) {
        return; // Don't close if clicking on date picker elements
      }

      if (startDateRef.current && !startDateRef.current.contains(event.target)) {
        setIsStartDatePickerOpen(false);
      }
      if (endDateRef.current && !endDateRef.current.contains(event.target)) {
        setIsEndDatePickerOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={`d-flex align-items-center gap-3 ${styles.filterBarContainer}`}
    >
      {/* <DropdownButton
        id="filter-type-dropdown"
        title={filterType}
        className={styles.dropdownBtn}
        variant="outline-secondary"
      >
        <Dropdown.Item onClick={() => handleFilterType('Monthly')}>Monthly</Dropdown.Item>
        <Dropdown.Item onClick={() => handleFilterType('Daily')}>Daily</Dropdown.Item>
      </DropdownButton> */}
      {showExtraFilter && (
        <SelectComponent
          options={[
            { label: "All", value: "All" },
            { label: "Monthly", value: "Monthly" },
            { label: "Daily", value: "Daily" },
          ]}
          placeholder="Type"
          handleValueChange={handleFilterType}
          value={filterType}
          inputColumn="filterType"
          getSuggestionData={() => {}}
          clearValueHandler={() => {}}
          onFocusHandler={() => {}}
          isMulti={false}
          isClearable={false}
        />
      )}
      <div className={styles.datePickerWrapper}>
        <button
          ref={startDateRef}
          type="button"
          onClick={handleStartDateInputClick}
          className={styles.dateInput}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            width: "100%",
            cursor: "pointer",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            padding: "4px 12px",
            fontSize: "13px",
            background: "transparent",
            outline: "none",
            color: startDate ? "#212529" : "#adb5bd",
          }}
        >
          <span role="img" aria-label="calendar">
            📆
          </span>
          <span>
            {startDate ? startDate.toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: 'short', 
              year: 'numeric' 
            }) : showEndDate ? "Start Date" : "Date"}
          </span>
        </button>
      </div>
      {showEndDate && (
        <div className={styles.datePickerWrapper}>
          <button
            ref={endDateRef}
            type="button"
            onClick={handleEndDateInputClick}
            disabled={!startDate}
            className={styles.dateInput}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              cursor: startDate ? "pointer" : "not-allowed",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "4px 12px",
              fontSize: "13px",
              background: startDate ? "transparent" : "#f5f5f5",
              outline: "none",
              color: startDate ? (endDate ? "#212529" : "#adb5bd") : "#b0b7c3",
              opacity: startDate ? 1 : 0.7,
            }}
          >
            <span role="img" aria-label="calendar">
              📆
            </span>
            <span>
              {endDate ? endDate.toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              }) : "End Date"}
            </span>
          </button>
        </div>
      )}
      <Modal
        show={showEndDatePrompt && !endDate}
        onHide={() => setShowEndDatePrompt(false)}
        centered
      >
        <Modal.Body className="text-center">
          <p>Please select an end date to complete the range.</p>
          <Button variant="primary" onClick={() => setShowEndDatePrompt(false)}>
            OK
          </Button>
        </Modal.Body>
      </Modal>

             {/* Portal-based Date Pickers */}
       {isStartDatePickerOpen && startDateRef.current && createPortal(
         <div
           style={{
             position: "fixed",
             top: startDateRef.current.getBoundingClientRect().bottom + 5,
             left: startDateRef.current.getBoundingClientRect().left,
             zIndex: 9999,
             background: "white",
             border: "1px solid #d1d5db",
             borderRadius: "8px",
             boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
           }}
         >
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            padding: "8px 12px",
            borderBottom: "1px solid #e5e7eb"
          }}>
            <span style={{ fontWeight: "500", color: "#374151" }}>Select Start Date</span>
            <button
              onClick={() => setIsStartDatePickerOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "#6b7280",
                padding: "0",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              ×
            </button>
          </div>
                     <DatePicker
             selected={startDate}
             onChange={handleStartDate}
             inline
             dateFormat="dd MMM yyyy"
             calendarClassName={styles.calendarPopup}
           />
        </div>,
        document.body
      )}

             {isEndDatePickerOpen && endDateRef.current && createPortal(
         <div
           style={{
             position: "fixed",
             top: endDateRef.current.getBoundingClientRect().bottom + 5,
             left: endDateRef.current.getBoundingClientRect().left,
             zIndex: 9999,
             background: "white",
             border: "1px solid #d1d5db",
             borderRadius: "8px",
             boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
           }}
         >
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            padding: "8px 12px",
            borderBottom: "1px solid #e5e7eb"
          }}>
            <span style={{ fontWeight: "500", color: "#374151" }}>Select End Date</span>
            <button
              onClick={() => setIsEndDatePickerOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "#6b7280",
                padding: "0",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              ×
            </button>
          </div>
                     <DatePicker
             selected={endDate}
             onChange={handleEndDate}
             inline
             dateFormat="dd MMM yyyy"
             calendarClassName={styles.calendarPopup}
             minDate={startDate}
           />
        </div>,
        document.body
      )}
    </div>
  );
};

export default FilterBar;
